import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import {
  Container,
  Spinner,
  Alert,
  Table,
  Row,
  Col,
  Card,
  Image
} from "react-bootstrap";

const UserReportDetails = () => {
  const { id } = useParams();
  const { user: currentUser } = useAuth();
  const { t } = useTranslation();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [debts, setDebts] = useState([]);
  const [showVatInfo, setShowVatInfo] = useState(false);

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!currentUser?.token) {
      setError(t("notAuthorized"));
      setLoading(false);
      return;
    }

    axios
      .get(`https://godrive.com.pl/api/user/report/${id}`, {
        headers: {
          Authorization: `Bearer ${currentUser.token}`,
        },
      })
      .then((res) => {
        setReport(res.data);
        const debtIds = res.data?.report_json?.selectedDebts;
        if (Array.isArray(debtIds) && debtIds.length > 0) {
          axios
            .get(`https://godrive.com.pl/api/debts/byIds?ids=${debtIds.join(",")}`, {
              headers: {
                Authorization: `Bearer ${currentUser.token}`,
              },
            })
            .then((debtRes) => setDebts(debtRes.data))
            .catch((err) => console.error("Помилка при завантаженні боргів:", err));
        }
      })
      .catch((err) => {
        console.error("Помилка:", err);
        setError(t("fetchReportError"));
      })
      .finally(() => setLoading(false));
  }, [id, currentUser, t]);

  if (loading) return <Spinner animation="border" variant="primary" className="m-4" />;
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!report) return null;

  const reportJson = report.report_json || {};
  const taxData = reportJson.taxData || {};
  const services = ["uber", "bolt", "freenow"];

  const serviceData = {
    uber: {
      brutto: parseFloat(reportJson?.report?.uber?.[0]?.incBrutto || 0),
      payout: parseFloat(reportJson?.report?.uber?.[0]?.incNetto || 0),
      vat: parseFloat(taxData?.uber?.tax || 0),
      total: parseFloat(taxData?.uber?.total || 0),
    },
    bolt: {
      brutto: parseFloat(reportJson?.report?.bolt?.[0]?.incBrutto || 0),
      payout: parseFloat(reportJson?.report?.bolt?.[0]?.totalInc || 0),
      vat: parseFloat(taxData?.bolt?.tax || 0),
      total: parseFloat(taxData?.bolt?.total || 0),
    },
    freenow: {
      brutto: parseFloat(reportJson?.report?.freenow?.[0]?.brutto || 0),
      payout: parseFloat(reportJson?.report?.freenow?.[0]?.saldo_freenow || 0),
      vat: parseFloat(taxData?.freenow?.tax || 0),
      total: parseFloat(taxData?.freenow?.total || 0),
    },
  };

  const extraFunds = reportJson.extraFunds || {};
  const filteredExtraFunds = Object.entries(extraFunds).filter(([key, val]) => {
    if (key === "other" && Array.isArray(extraFunds.other_expenses) && extraFunds.other_expenses.length > 0) {
      return false; // прибираємо дублікат
    }

    if (Array.isArray(val)) return val.length > 0;
    return parseFloat(val) !== 0;
  });

  const getServiceLogo = (service) => `/images/services/${service}_logo.png`;

  return (
    <Container className="mt-4">
      <h2>{t("reportForWeek")} {report.week_number}</h2>
      <p><strong>{t("createdAt")}:</strong> {new Date(report.created_at).toLocaleDateString()}</p>

      {/* Сервіси */}
      <Row className="mt-4">
        {services.map((service) => (
          <Col md={4} key={service}>
            <Card className="mb-3">
              <Card.Body>
                <div className="text-center mb-3">
                  <Image src={getServiceLogo(service)} height={60} alt={service} />
                </div>
                <p><strong>{t("brutto")}:</strong> {serviceData[service].brutto.toFixed(2)} zł</p>

                <p><strong>{t("payout")} {service}:</strong> {serviceData[service].payout.toFixed(2)} zł</p>
                <p><strong>VAT 4%</strong>: {serviceData[service].vat.toFixed(2)} zł</p>



                <p><strong>{t("amount")}:</strong> {serviceData[service].total.toFixed(2)} zł</p>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
      <Alert variant="info" className="mb-4">
        <strong>ℹ {t("explanationTitle")}:</strong>
        <ul className="mb-0 mt-2 ps-3">
          <li><strong>{t("brutto")}:</strong> {t("explanationBrutto")}</li>
          <li><strong>{t("payout")}:</strong> {t("explanationPayout")}</li>

          <li><strong>VAT:</strong> {t("explanationVAT")}</li>
          <li><strong>{t("amount")}:</strong> {t("explanationTotal")}</li>
        </ul>
      </Alert>

      {/* ExtraFunds */}
      {filteredExtraFunds.length > 0 && (
        <Card className="mb-4">
          <Card.Body>
            <h5>{t("additionalFunds")}</h5>
            <Table bordered>
              <tbody>
                {filteredExtraFunds.map(([key, val]) => (
                  <tr key={key}>
                    <td>{t(`extraFunds.${key}`, { defaultValue: key })}</td>
                    <td>
                      {Array.isArray(val)
                        ? val.map((item, idx) => (
                          <div key={idx}>
                            {item.description}: {item.amount} zł
                          </div>
                        ))
                        : `${val} zł`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Враховані борги */}
      {debts.length > 0 && (
        <Card className="mb-4">
          <Card.Body>
            <h5>{t("includedDebts")}</h5>
            <Table bordered>
              <thead>
                <tr>
                  <th>{t("title")}</th>
                  <th>{t("description")}</th>
                  <th>{t("amount")}</th>
                  <th>{t("date")}</th>
                </tr>
              </thead>
              <tbody>
                {debts.map((debt) => (
                  <tr key={debt.id}>
                    <td>{debt.description}</td>
                    <td>{debt.details || "-"}</td>
                    <td>{debt.amount} zł</td>
                    <td>{new Date(debt.date).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>

            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Payout */}
      <Card className="mb-5 bg-success-subtle ">
        <Card.Body >
          <h5 className="text-success">{t("payoutToAccount")}:</h5>
          <h3>{reportJson.payout} zł</h3>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default UserReportDetails;
